// Simple API service for sending HTML content from editor

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';

// Send HTML content to API
export const sendHtmlContent = async (documentId, htmlContent, options = {}) => {
  const url = `${API_BASE_URL}/documents/${documentId}/content`;
  
  const payload = {
    documentId,
    htmlContent,
    timestamp: new Date().toISOString(),
    ...options
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error sending HTML content:', error);
    throw error;
  }
};

// Save HTML content as draft
export const saveHtmlDraft = async (documentId, htmlContent) => {
  return sendHtmlContent(documentId, htmlContent, { 
    type: 'draft',
    status: 'saved'
  });
};

// Publish HTML content
export const publishHtmlContent = async (documentId, htmlContent) => {
  return sendHtmlContent(documentId, htmlContent, { 
    type: 'published',
    status: 'live'
  });
};

// Helper function to preserve spaces in HTML by converting multiple spaces to &nbsp;
const preserveSpacesInHTML = (html) => {
  if (!html) return html;
  
  // Use DOMParser to properly handle HTML and preserve spaces in text nodes
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Function to process text nodes and preserve multiple spaces and trailing spaces
  const processTextNode = (textNode) => {
    if (textNode.nodeType !== 3) return; // Not a text node
    
    const text = textNode.textContent;
    let preserved = text;
    
    // Replace 2+ consecutive spaces with &nbsp; entities
    // This preserves multiple spaces while keeping single spaces between words
    preserved = preserved.replace(/ {2,}/g, (match) => {
      return '&nbsp;'.repeat(match.length);
    });
    
    // Also preserve trailing spaces (spaces at the end of text)
    // HTML collapses trailing spaces, so we need to convert them to &nbsp;
    preserved = preserved.replace(/ +$/g, (match) => {
      return '&nbsp;'.repeat(match.length);
    });
    
    if (preserved !== text) {
      // Create a temporary container to convert &nbsp; entities to actual HTML
      const temp = document.createElement('div');
      temp.innerHTML = preserved;
      
      // Replace the text node with the preserved content
      const fragment = document.createDocumentFragment();
      while (temp.firstChild) {
        fragment.appendChild(temp.firstChild);
      }
      textNode.parentNode.replaceChild(fragment, textNode);
    }
  };
  
  // Process all text nodes in the document
  const walker = document.createTreeWalker(
    doc.body,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );
  
  const textNodes = [];
  let node;
  while (node = walker.nextNode()) {
    textNodes.push(node);
  }
  
  // Process nodes in reverse to avoid issues with DOM manipulation
  for (let i = textNodes.length - 1; i >= 0; i--) {
    processTextNode(textNodes[i]);
  }
  
  return doc.body.innerHTML;
};

// Get HTML content from editor
export const getHtmlFromEditor = (quillRef) => {
  if (!quillRef?.current?.getEditor) {
    throw new Error('Invalid Quill editor reference');
  }
  
  const editor = quillRef.current.getEditor();
  const html = editor.root.innerHTML;
  
  // Preserve spaces in the HTML before returning
  // Convert multiple spaces to &nbsp; to prevent HTML from collapsing them
  return preserveSpacesInHTML(html);
};

// Send HTML with additional metadata
export const sendHtmlWithMetadata = async (documentId, quillRef) => {
  const editor = quillRef.current.getEditor();
  const htmlContent = editor.root.innerHTML;
  const plainText = editor.getText();
  
  const payload = {
    documentId,
    htmlContent,
    plainText,
    wordCount: plainText.split(/\s+/).length,
    characterCount: plainText.length,
    timestamp: new Date().toISOString(),
  };

  return sendHtmlContent(documentId, htmlContent, payload);
};
